// Design tokens — single source of truth for colors, spacing, typography.
//
// Colours are the phosphor-dark palette adopted from dse-terminal; the raw values live in
// styles/phosphor.ts and this file gives them semantic names. One deliberate departure:
// phosphor uses amber for BOTH chrome and warnings, so warnings are re-homed to a hot orange
// (colors.accent.yellow / colors.status.warning) and amber is chrome-only (colors.chrome).

import { phosphor, phosphorFonts } from './phosphor';

export const colors = {
  bg: {
    primary:   phosphor.page,       // page background
    secondary: phosphor.panel,      // card / panel background
    tertiary:  phosphor.line,       // hover / active states
    inset:     phosphor.inset,      // recessed wells inside a panel
    overlay:   `${phosphor.page}cc`,
  },
  text: {
    primary:   phosphor.ink,
    bright:    phosphor.inkBright,
    secondary: phosphor.muted,
    muted:     phosphor.textDim,    // dimmest ink that still clears WCAG AA
    faint:     phosphor.faint,      // DECORATIVE/DISABLED ONLY — fails AA
    inverted:  phosphor.onAccent,
  },
  accent: {
    green:  phosphor.green,
    red:    phosphor.red,
    /**
     * WARNING. Re-homed to a hot orange so it cannot be confused with the chrome amber
     * below — dse-terminal spends one hue on both, which does not survive our ~140
     * warning surfaces.
     */
    yellow: phosphor.warn,
    blue:   phosphor.blue,
    purple: phosphor.tox,
    cyan:   phosphor.cyan,
  },
  /** Chrome / brand. Nav rail, panel headers, badges, focus glow. NOT a semantic state. */
  chrome: {
    base: phosphor.amber,
    hi:   phosphor.amberHi,
  },
  /** 15%-over-panel tints for filled badges and callout backgrounds. */
  soft: {
    blue:  phosphor.blueSoft,
    green: phosphor.greenSoft,
    red:   phosphor.redSoft,
    amber: phosphor.amberSoft,
    warn:  phosphor.warnSoft,
    tox:   phosphor.toxSoft,
  },
  border: {
    default: phosphor.lineStrong,
    subtle:  phosphor.line,
    focus:   phosphor.blue,
  },
  status: {
    success:  phosphor.green,
    warning:  phosphor.warn,
    error:    phosphor.red,
    info:     phosphor.blue,
    positive: phosphor.green,
    critical: phosphor.red,
  },
} as const;

export const spacing = {
  xs:  '4px',
  sm:  '8px',
  md:  '12px',
  lg:  '16px',
  xl:  '24px',
  '2xl': '32px',
  '3xl': '48px',
} as const;

export const radius = {
  sm: '4px',
  md: '6px',
  lg: '8px',
  xl: '12px',
  full: '9999px',
} as const;

export const font = {
  family: {
    // Loaded from Google Fonts in index.css. We previously declared JetBrains Mono here and
    // never loaded it anywhere, so every "mono" figure fell back to the system default.
    mono: phosphorFonts.mono,
    sans: phosphorFonts.display,
    display: phosphorFonts.display,
  },
  size: {
    xs:   '10px',
    sm:   '11px',
    base: '12px',
    md:   '13px',
    lg:   '14px',
    xl:   '16px',
    '2xl': '18px',
    '3xl': '20px',
  },
  weight: {
    normal:   400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },
} as const;

/** Semantic severity → color mapping (matches ActivitySeverity). */
export const severityColor: Record<string, string> = {
  info:     colors.text.secondary,
  warning:  colors.accent.yellow,
  critical: colors.accent.red,
  positive: colors.accent.green,
};

/** Side → color (buy = green, sell = red). */
export const sideColor = {
  buy:  colors.accent.green,
  sell: colors.accent.red,
} as const;

/** PnL sign → color. */
export function pnlColor(value: number): string {
  if (value > 0) return colors.accent.green;
  if (value < 0) return colors.accent.red;
  return colors.text.secondary;
}
